import { makeAutoObservable } from 'mobx';

import { taxes } from '@/helpers/oddsConverter/taxes';
import { expressMaxOdd } from '@/config/constants';

class BetslipBonus {
  root: RootStoreMobx;

  constructor(model: RootStoreMobx) {
    makeAutoObservable(this);

    this.root = model;
  }

  isBonusBet = false;

  get bonusTotalWin() {
    const { roundCurrency } = this.root.profile;
    const { hasTaxes } = this.root.ui;
    const { brand } = this.root.auth;
    const { currentTab, betsWithBonus } = this.root.betslip;

    let totalWin = 0;

    if (currentTab === 'SINGLEBET') {
      totalWin = betsWithBonus.reduce((acc, { bonus, bet }) => {
        if (bonus.minSelection > 1 || !bet) {
          return acc;
        }
        const { id, pidLid } = bet;
        const betItem = this.root.store.getOutcomeByLID(id ?? '', pidLid ?? '');
        if (!betItem || betItem?.isFrozen) {
          return acc;
        }

        let possibleWin = bonus.betAmount * betItem.price - bonus.betAmount;

        if (hasTaxes && taxes) {
          const betTaxes = taxes[brand]?.getTaxes(bonus.betAmount, betItem.price, roundCurrency);
          if (betTaxes) {
            possibleWin = Number(betTaxes.possibleWin);
          }
        }

        if (possibleWin <= 0) {
          possibleWin = 0;
        }

        return acc + possibleWin;
      }, 0);
    }

    if (currentTab === 'EXPRESS' && !!betsWithBonus.length) {
      const firstBet = betsWithBonus[0];
      if (!firstBet) {
        this.root.betslip.isBetSlipWithBonuses = false;
        // here you need to specify what error to display
        return 0;
      }
      const { bonus: { betAmount = 0 } = {} } = firstBet;
      const expressBonusTotalWin: number = betsWithBonus.reduce(
        (acc: number, { bet }: BetWithBonus) => {
          if (!bet || bet.isFrozen) {
            return acc;
          }
          return acc * bet.price;
        },
        1,
      );

      const cappedCoeff = Math.min(expressBonusTotalWin, expressMaxOdd);
      const rawWin = betAmount * cappedCoeff - betAmount;
      totalWin = roundCurrency(Math.max(0, rawWin));
    }

    if (!totalWin) {
      this.root.betslip.isBetSlipWithBonuses = false;
      // here you need to specify what error to display
      return 0;
    }

    return roundCurrency(totalWin);
  }

  setIsBonusBet = (bool: boolean = false) => {
    this.isBonusBet = bool;
  };

  placeBonusBet = async (bonusBet: BetWithBonus[]): Promise<void> => {
    if (!Array.isArray(bonusBet) || !bonusBet.length) {
      return;
    }

    this.isBonusBet = true;

    const firstBet = bonusBet[0];
    if (!firstBet) {
      return;
    }

    const { bonus: { betAmount = 0 } = {} } = firstBet;
    this.root.betslip.bonusBetInProgressId = firstBet.bonus.id;

    if (this.root.betslip.currentTab === 'SINGLEBET') {
      const bets: Array<defaultBet & { bonusId: string; stake: number }> = bonusBet.reduce(
        (acc: Array<defaultBet & { bonusId: string; stake: number }>, { bonus, bet }) => {
          if (bonus.minSelection > 1 || !bet) {
            return acc;
          }
          acc.push({
            ...bet,
            bonusId: bonus.id,
            stake: bonus.betAmount,
          });
          return acc;
        },
        [],
      );
      this.root.betslip.makeBet(false, bets);

      return;
    }

    this.root.betslip.expressAmount = betAmount;
    this.root.betslip.makeExpressBet({
      bets: bonusBet.map(({ bet }) => bet).filter((b): b is defaultBet => Boolean(b)),
      bonusId: firstBet.bonus.id,
    });
  };
}

export { BetslipBonus };

interface defaultBet {
  price: number;
  isFrozen?: boolean;
  id?: string;
  pidLid?: string;
}

interface BonusConfiguration {
  id: string;
  betAmount: number;
  minSelection: number;
  maxWin?: number;
}

interface BetWithBonus {
  bet?: defaultBet;
  bonus: BonusConfiguration;
}

interface RootStoreMobx {
  profile: {
    roundCurrency: (value: number) => number;
  };
  ui: {
    hasTaxes: boolean;
  };
  auth: {
    brand: string;
  };
  betslip: {
    currentTab: string;
    betsWithBonus: BetWithBonus[];
    isBetSlipWithBonuses: boolean;
    setNotice: (noticeType: string, value: boolean) => void;
    bonusBetInProgressId: string;
    expressAmount: number;
    makeBet: (param: boolean, bets: defaultBet[]) => void;
    makeExpressBet: (params: { bets: defaultBet[]; bonusId: string }) => void;
  };
  store: {
    getOutcomeByLID: (id: string, pidLid: string) => defaultBet | undefined;
  };
}
